import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Plus, Send, Calendar, Clock, Users, Filter, 
  X, Loader2, CheckCircle, Mail, MessageSquare, Play, Pause, Trash2,
  ChevronDown, ChevronUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const MarketingCampaigns = () => {
  const { token, getAuthHeaders, user, isCalendarAdmin } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    template_id: '',
    subject: '',
    body: '',
    channel: 'email',
    schedule_type: 'immediate',
    schedule_time: '09:00',
    schedule_day_of_week: 0,
    schedule_day_of_month: 1,
    filter_type: 'all',
    filter_days: 30,
    filter_service_id: '',
    filter_date: '',
    filter_start_time: '',
    filter_end_time: '',
    selected_customers: []
  });

  useEffect(() => {
    if (!isCalendarAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [isCalendarAdmin, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, templatesRes, servicesRes, customersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/marketing/campaigns`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/marketing/templates`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/services`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE_URL}/auth/users`, { headers: getAuthHeaders() })
      ]);
      
      if (campaignsRes.data.success) setCampaigns(campaignsRes.data.data);
      if (templatesRes.data.success) setTemplates(templatesRes.data.data);
      if (servicesRes.data.success) setServices(servicesRes.data.data);
      if (customersRes.data.success) {
        setCustomers(customersRes.data.data.filter(u => u.role === 'customer'));
      }
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      template_id: '',
      subject: '',
      body: '',
      channel: 'email',
      schedule_type: 'immediate',
      schedule_time: '09:00',
      schedule_day_of_week: 0,
      schedule_day_of_month: 1,
      filter_type: 'all',
      filter_days: 30,
      filter_service_id: '',
      filter_date: '',
      filter_start_time: '',
      filter_end_time: '',
      selected_customers: []
    });
    setPreviewCount(null);
    setShowModal(true);
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        template_id: templateId,
        subject: template.subject,
        body: template.body,
        channel: template.channel || 'email'
      }));
    } else {
      setFormData(prev => ({ ...prev, template_id: templateId }));
    }
  };

  const buildRecipientFilter = () => {
    const filter = { filter_type: formData.filter_type };
    
    if (formData.filter_type === 'manual') {
      filter.customer_ids = formData.selected_customers;
    } else if (formData.filter_type === 'last_n_days') {
      filter.days = parseInt(formData.filter_days);
    } else if (formData.filter_type === 'service') {
      filter.service_id = formData.filter_service_id;
    } else if (formData.filter_type === 'time_slot') {
      filter.date = formData.filter_date;
      filter.start_time = formData.filter_start_time;
      filter.end_time = formData.filter_end_time;
    }
    
    return filter;
  };

  const previewRecipients = async () => {
    try {
      setPreviewLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/marketing/preview-recipients`,
        buildRecipientFilter(),
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        setPreviewCount(response.data.data.count);
      }
    } catch (err) {
      setError('Failed to preview recipients');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name) {
      setError('Campaign name is required');
      return;
    }
    if (!formData.subject && !formData.template_id) {
      setError('Subject is required');
      return;
    }
    if (!formData.body && !formData.template_id) {
      setError('Message body is required');
      return;
    }

    try {
      setSending(true);
      setError('');
      
      const payload = {
        name: formData.name,
        template_id: formData.template_id || null,
        subject: formData.subject,
        body: formData.body,
        channel: formData.channel,
        recipient_filter: buildRecipientFilter(),
        schedule: {
          schedule_type: formData.schedule_type,
          time: formData.schedule_time,
          day_of_week: formData.schedule_day_of_week,
          day_of_month: formData.schedule_day_of_month
        }
      };

      await axios.post(
        `${API_BASE_URL}/marketing/campaigns`,
        payload,
        { headers: getAuthHeaders() }
      );
      
      setSuccess(formData.schedule_type === 'immediate' 
        ? 'Campaign sent successfully!' 
        : 'Campaign scheduled successfully!');
      setShowModal(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (campaignId, newStatus) => {
    try {
      await axios.put(
        `${API_BASE_URL}/marketing/campaigns/${campaignId}/status?status_update=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess(`Campaign ${newStatus}`);
      fetchData();
    } catch (err) {
      setError('Failed to update campaign status');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/marketing/campaigns/${campaignId}`, {
        headers: getAuthHeaders()
      });
      setSuccess('Campaign deleted');
      fetchData();
    } catch (err) {
      setError('Failed to delete campaign');
    }
  };

  const toggleCustomer = (customerId) => {
    setFormData(prev => ({
      ...prev,
      selected_customers: prev.selected_customers.includes(customerId)
        ? prev.selected_customers.filter(id => id !== customerId)
        : [...prev.selected_customers, customerId]
    }));
  };

  const getScheduleText = (schedule) => {
    if (!schedule) return 'One-time';
    const type = schedule.schedule_type;
    if (type === 'immediate') return 'One-time (sent)';
    if (type === 'daily') return `Daily at ${schedule.time || '09:00'}`;
    if (type === 'weekly') {
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      return `Weekly on ${days[schedule.day_of_week || 0]} at ${schedule.time || '09:00'}`;
    }
    if (type === 'monthly') return `Monthly on day ${schedule.day_of_month || 1} at ${schedule.time || '09:00'}`;
    return type;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <button
            onClick={() => navigate('/marketing')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketing
          </button>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Marketing Campaigns</h1>
              <p className="text-gray-600 mt-2">Create ad-hoc or scheduled marketing campaigns</p>
            </div>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold transition-colors"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Campaign
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
              <button onClick={() => setError('')} className="float-right">&times;</button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
              <button onClick={() => setSuccess('')} className="float-right">&times;</button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Send className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Campaigns Yet</h3>
              <p className="text-gray-500 mb-4">Create your first marketing campaign</p>
              <button
                onClick={handleCreate}
                className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-semibold transition-colors"
              >
                Create Campaign
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Channel</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stats</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {campaigns.map(campaign => (
                    <tr key={campaign.id} data-testid={`campaign-${campaign.id}`}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{campaign.name}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">{campaign.subject}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="flex items-center text-sm">
                          {campaign.channel === 'sms' ? (
                            <MessageSquare className="w-4 h-4 mr-1 text-blue-500" />
                          ) : campaign.channel === 'both' ? (
                            <>
                              <Mail className="w-4 h-4 mr-1 text-yellow-500" />
                              <MessageSquare className="w-4 h-4 mr-1 text-blue-500" />
                            </>
                          ) : (
                            <Mail className="w-4 h-4 mr-1 text-yellow-500" />
                          )}
                          {campaign.channel}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {getScheduleText(campaign.schedule)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="text-green-600">{campaign.total_sent || 0} sent</span>
                        {campaign.total_failed > 0 && (
                          <span className="text-red-600 ml-2">{campaign.total_failed} failed</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                          campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                          campaign.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          {campaign.status === 'active' && (
                            <button
                              onClick={() => handleStatusChange(campaign.id, 'paused')}
                              className="p-1 text-yellow-600 hover:bg-yellow-50 rounded"
                              title="Pause"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {campaign.status === 'paused' && (
                            <button
                              onClick={() => handleStatusChange(campaign.id, 'active')}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                              title="Resume"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(campaign.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Create Campaign Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">Create Campaign</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Campaign Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                  placeholder="e.g., January Promotion"
                  data-testid="campaign-name-input"
                />
              </div>

              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Use Template (Optional)</label>
                <select
                  value={formData.template_id}
                  onChange={e => handleTemplateChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">-- Write custom message --</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* Channel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                <div className="flex gap-4">
                  {['email', 'sms', 'both'].map(ch => (
                    <label key={ch} className="flex items-center">
                      <input
                        type="radio"
                        value={ch}
                        checked={formData.channel === ch}
                        onChange={e => setFormData({ ...formData, channel: e.target.value })}
                        className="w-4 h-4 text-yellow-500"
                      />
                      <span className="ml-2 text-sm capitalize">{ch}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Subject & Body */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={e => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500"
                  placeholder="Email subject line"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message Body *</label>
                <textarea
                  value={formData.body}
                  onChange={e => setFormData({ ...formData, body: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 font-mono text-sm"
                  rows={5}
                  placeholder="Hi &customer-name, ..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Use <code className="bg-gray-100 px-1 rounded">&customer-name</code> for personalization
                </p>
              </div>

              {/* Schedule */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Clock className="w-5 h-5 text-gray-500 mr-2" />
                  <span className="font-medium text-gray-700">Schedule</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Type</label>
                    <select
                      value={formData.schedule_type}
                      onChange={e => setFormData({ ...formData, schedule_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="immediate">Send Now</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  
                  {formData.schedule_type !== 'immediate' && (
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Time</label>
                      <input
                        type="time"
                        value={formData.schedule_time}
                        onChange={e => setFormData({ ...formData, schedule_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  )}
                  
                  {formData.schedule_type === 'weekly' && (
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Day of Week</label>
                      <select
                        value={formData.schedule_day_of_week}
                        onChange={e => setFormData({ ...formData, schedule_day_of_week: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((d, i) => (
                          <option key={i} value={i}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {formData.schedule_type === 'monthly' && (
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Day of Month</label>
                      <select
                        value={formData.schedule_day_of_month}
                        onChange={e => setFormData({ ...formData, schedule_day_of_month: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Recipient Targeting */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedFilters(!expandedFilters)}
                >
                  <div className="flex items-center">
                    <Filter className="w-5 h-5 text-blue-500 mr-2" />
                    <span className="font-medium text-gray-700">Recipient Targeting</span>
                  </div>
                  {expandedFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>

                {expandedFilters && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Filter Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: 'all', label: 'All Customers' },
                          { value: 'manual', label: 'Manual Selection' },
                          { value: 'last_n_days', label: 'Booked Last N Days' },
                          { value: 'service', label: 'By Service' },
                          { value: 'time_slot', label: 'By Time Slot' }
                        ].map(opt => (
                          <label key={opt.value} className="flex items-center p-2 bg-white rounded border cursor-pointer hover:bg-gray-50">
                            <input
                              type="radio"
                              value={opt.value}
                              checked={formData.filter_type === opt.value}
                              onChange={e => setFormData({ ...formData, filter_type: e.target.value })}
                              className="w-4 h-4 text-yellow-500"
                            />
                            <span className="ml-2 text-sm">{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Filter-specific inputs */}
                    {formData.filter_type === 'last_n_days' && (
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Days</label>
                        <input
                          type="number"
                          value={formData.filter_days}
                          onChange={e => setFormData({ ...formData, filter_days: parseInt(e.target.value) })}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          min={1}
                          max={365}
                        />
                      </div>
                    )}

                    {formData.filter_type === 'service' && (
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Service</label>
                        <select
                          value={formData.filter_service_id}
                          onChange={e => setFormData({ ...formData, filter_service_id: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="">-- Select Service --</option>
                          {services.map(s => (
                            <option key={s.id} value={s.id}>{s.service_name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {formData.filter_type === 'time_slot' && (
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Date</label>
                          <input
                            type="date"
                            value={formData.filter_date}
                            onChange={e => setFormData({ ...formData, filter_date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Start Time</label>
                          <input
                            type="time"
                            value={formData.filter_start_time}
                            onChange={e => setFormData({ ...formData, filter_start_time: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">End Time</label>
                          <input
                            type="time"
                            value={formData.filter_end_time}
                            onChange={e => setFormData({ ...formData, filter_end_time: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                    )}

                    {formData.filter_type === 'manual' && (
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm text-gray-600">
                            Select Customers ({formData.selected_customers.length} selected)
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, selected_customers: customers.map(c => c.id) })}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Select All
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, selected_customers: [] })}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white">
                          {customers.map(c => (
                            <label key={c.id} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer border-b last:border-0">
                              <input
                                type="checkbox"
                                checked={formData.selected_customers.includes(c.id)}
                                onChange={() => toggleCustomer(c.id)}
                                className="w-4 h-4 text-yellow-500"
                              />
                              <div className="ml-2">
                                <div className="text-sm font-medium">{c.full_name}</div>
                                <div className="text-xs text-gray-500">{c.email}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Preview Recipients Button */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <button
                        type="button"
                        onClick={previewRecipients}
                        disabled={previewLoading}
                        className="text-sm text-blue-600 hover:underline flex items-center"
                      >
                        {previewLoading ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Users className="w-4 h-4 mr-1" />
                        )}
                        Preview Recipients
                      </button>
                      {previewCount !== null && (
                        <span className="text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 inline mr-1 text-green-500" />
                          {previewCount} recipients found
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={sending}
                className="flex items-center px-6 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white font-semibold rounded-md transition-colors"
                data-testid="save-campaign-btn"
              >
                {sending ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                {sending ? 'Processing...' : formData.schedule_type === 'immediate' ? 'Send Now' : 'Schedule Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MarketingCampaigns;
